import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { throttle } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import type { ImageCropData, PopupData } from 'webapp-types';

import eq from 'webapp/helpers/eq';
import CommonImageUploadUploadComponent from 'webapp/components/common/image-upload/upload';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import { CoverAction } from 'webapp/components/reports/cover-pageless';
import SubmitButtonComponent from 'webapp/components/submit-button';

enum SelectedTab {
	GALLERY = 'gallery',
	UPLOAD = 'upload',
}

export default class ReportPopupCoverComponent extends PopupInnerModalComponent<PopupData.Report.Cover> {
	@tracked isCropActive = false;
	@tracked selectedTab = this.isActionAdd ? SelectedTab.GALLERY : SelectedTab.UPLOAD;
	@tracked selectedImage: string | null = null;

	tabs = [
		{ id: SelectedTab.GALLERY, text: 'Gallery' },
		{ id: SelectedTab.UPLOAD, text: this.isActionAdd ? 'Upload' : 'Edit cover' },
	];

	coverImageData: { title: string; images: string[] }[] = [
		{
			title: 'Color & gradient',
			images: ['1', '44', '43', '42', '41', '40', '39', '38', '37', '36', '35', '34'],
		},
		{
			title: 'Images',
			images: [
				'33',
				'32',
				'31',
				'30',
				'29',
				'28',
				'27',
				'26',
				'25',
				'24',
				'23',
				'22',
				'21',
				'20',
				'19',
				'18',
				'17',
				'16',
				'15',
				'14',
				'13',
				'12',
				'11',
				'10',
				'9',
				'8',
				'7',
				'6',
				'5',
				'4',
				'3',
				'2',
			],
		},
	];

	get action() {
		return this.popupData.action;
	}

	get isActionAdd() {
		return this.action === CoverAction.ADD;
	}

	get isSaveDisabled() {
		return !this.selectedImage;
	}

	get isSelectedTabGallery() {
		return this.selectedTab === SelectedTab.GALLERY;
	}

	get isDeleteEnabled() {
		return this.popupData.imageOriginalUrl;
	}

	@action
	save() {
		if (this.selectedImage) {
			const url = `https://cdn1.databox.com/analytics/report/cover/cover_${this.selectedImage}.jpg`;
			this.onFinish(url, url, null);
		}
	}

	@action
	delete() {
		this.onFinish('', '', null);
	}

	@action
	openTab(tabId: SelectedTab) {
		this.selectedTab = tabId;
		this.scheduleReposition();
	}

	@action
	selectImage(image: string) {
		this.selectedImage = image;
	}

	@action
	onImageCrop() {
		throttle(this, this.reposition, 500);
	}

	@action
	onCropActiveChange(isCropActive: boolean) {
		this.isCropActive = isCropActive;
	}

	@action
	onFinish(url: string, originalUrl: string, crop: ImageCropData | null) {
		this.popupData.onFinish(url, originalUrl, crop);
		this.onClose();
	}

	@action
	onClose() {
		this.close();
	}
	<template>
		<div class="title">
			<span>
				<CommonUiIconComponent @name="Image" @size="lg" />
				Choose image
			</span>
			<div class="tabs">
				<ul class="modalTabs modalTabs-2">
					{{#each this.tabs as |tab|}}
						<li
							class="navigation-button
								{{if (eq this.selectedTab tab.id) 'navigation-button--selected'}}"
						>
							<a role="button" {{on "click" (fn this.openTab tab.id)}}>
								{{tab.text}}
							</a>
						</li>
					{{/each}}
				</ul>
			</div>
		</div>

		<div class="content">
			{{#if this.isSelectedTabGallery}}
				<div class="covers">
					{{#each this.coverImageData as |section|}}
						<div class="section">
							<div class="section-title">
								{{section.title}}
							</div>
							<div class="section-images">
								{{#each section.images as |image|}}
									<a
										class="image
											{{if (eq this.selectedImage image) 'selected'}}"
										{{on "click" (fn this.selectImage image)}}
									>
										<img
											src={{concat
												"https://cdn1.databox.com/analytics/report/cover/cover_thumb_"
												image
												".png"
											}}
											alt={{concat "Cover image thumbnail_" image}}
										/>
									</a>
								{{/each}}
							</div>
						</div>
					{{/each}}
				</div>
			{{else}}
				<CommonImageUploadUploadComponent
					@imageOriginalUrl={{this.popupData.imageOriginalUrl}}
					@imageBlobUrl={{this.popupData.imageBlobUrl}}
					@imageCrop={{this.popupData.imageCrop}}
					@imageAspect={{this.popupData.imageAspect}}
					@onImageCrop={{this.onImageCrop}}
					@onFinish={{this.onFinish}}
					@onClose={{this.onClose}}
					@onCropActiveChange={{this.onCropActiveChange}}
				/>
			{{/if}}
		</div>

		{{#if this.isSelectedTabGallery}}
			<div class="controls2 controls2--sticky">
				{{#if this.isDeleteEnabled}}
					<div class="controls2__left">
						<SubmitButtonComponent
							@text="Delete"
							@icon="Trash"
							@theme="red-outlined"
							@onTrigger={{this.delete}}
						/>
					</div>
				{{/if}}

				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@onTrigger={{this.save}}
						@isDisabled={{this.isSaveDisabled}}
					/>
				</div>
			</div>
		{{/if}}
	</template>
}
